import { buildPrimaryFetchInit } from "../primary/fetchInit";
import { resolvePrimaryEnvironmentHttpUrl } from "../primary/target";
import { isCmmdAuthFailure, readCmmdAuthFailureCode } from "@cmmd-forge/shared/cmmdAuthFailure";
import { raceWithAbort, sleepWithAbort } from "./abortableWait";
import {
  CLOUD_RUNTIME_CONNECT_MAX_ATTEMPTS,
  cloudRuntimeRetryDelayMs,
  shouldRetryCloudRuntimeResponse,
} from "./cloudRuntimeConnectRetry";

const RUNTIME_TICKET_REFRESH_SKEW_MS = 60_000;

export interface CloudRuntimeConnectionDescriptor {
  readonly httpBaseUrl: string;
  readonly wsBaseUrl: string;
  readonly bearerToken: string;
  readonly expiresAt: string | null;
}

interface CloudRuntimeDescriptorRequest {
  readonly runtimeId: string;
  readonly organizationId?: string;
  readonly signal?: AbortSignal;
}

interface CloudRuntimeCredentialProviderInput {
  readonly runtimeId: string;
  readonly organizationId?: string;
  readonly initialDescriptor?: CloudRuntimeConnectionDescriptor;
  readonly now?: () => number;
  readonly requestDescriptor?: (
    input: CloudRuntimeDescriptorRequest,
  ) => Promise<CloudRuntimeConnectionDescriptor>;
  readonly validateDescriptor?: (descriptor: CloudRuntimeConnectionDescriptor) => Promise<void>;
  readonly persistDescriptor?: (descriptor: CloudRuntimeConnectionDescriptor) => Promise<void>;
}

export interface CloudRuntimeCredentialProvider {
  readonly acquire: (options?: {
    readonly forceRefresh?: boolean;
    readonly signal?: AbortSignal;
  }) => Promise<CloudRuntimeConnectionDescriptor>;
  readonly invalidate: () => void;
  readonly dispose: () => void;
}

function readErrorMessage(value: unknown, fallback: string): string {
  if (!value || typeof value !== "object") return fallback;
  const error = (value as { readonly error?: unknown }).error;
  return typeof error === "string" && error.trim().length > 0 ? error : fallback;
}

const INVALID_DESCRIPTOR_MESSAGE = "CMMD returned an invalid Cloud runtime connection descriptor.";

function readRequiredDescriptorString(descriptor: Record<string, unknown>, field: string): string {
  const value = descriptor[field];
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(INVALID_DESCRIPTOR_MESSAGE);
  }
  return value;
}

function parseDescriptor(value: unknown): CloudRuntimeConnectionDescriptor {
  if (!value || typeof value !== "object") {
    throw new Error(INVALID_DESCRIPTOR_MESSAGE);
  }
  const descriptor = value as Record<string, unknown>;
  return {
    httpBaseUrl: readRequiredDescriptorString(descriptor, "httpBaseUrl"),
    wsBaseUrl: readRequiredDescriptorString(descriptor, "wsBaseUrl"),
    bearerToken: readRequiredDescriptorString(descriptor, "bearerToken"),
    expiresAt: readRequiredDescriptorString(descriptor, "expiresAt"),
  };
}

export async function requestCloudRuntimeConnectionDescriptor(
  input: CloudRuntimeDescriptorRequest,
): Promise<CloudRuntimeConnectionDescriptor> {
  const url = resolvePrimaryEnvironmentHttpUrl(
    `/api/cmmd/forge-runtimes/${encodeURIComponent(input.runtimeId)}/connect`,
  );
  const request = buildPrimaryFetchInit({
    method: "POST",
    ...(input.signal ? { signal: input.signal } : {}),
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      input.organizationId === undefined ? {} : { organizationId: input.organizationId },
    ),
  });
  for (let attempt = 1; attempt <= CLOUD_RUNTIME_CONNECT_MAX_ATTEMPTS; attempt += 1) {
    const response = await fetch(url, request);
    const body = await response.json().catch(() => null);
    if (response.ok) return parseDescriptor(body);

    if (
      !shouldRetryCloudRuntimeResponse({
        status: response.status,
        body,
        attempt,
        maxAttempts: CLOUD_RUNTIME_CONNECT_MAX_ATTEMPTS,
      })
    ) {
      const fallback = isCmmdAuthFailure(response.status, body)
        ? "CMMD authentication expired while renewing the Cloud runtime connection. Sign in again."
        : `CMMD could not renew the Cloud runtime connection (${response.status}).`;
      throw Object.assign(new Error(readErrorMessage(body, fallback)), {
        status: response.status,
        ...(readCmmdAuthFailureCode(body) ? { code: readCmmdAuthFailureCode(body) } : {}),
      });
    }
    await sleepWithAbort(
      cloudRuntimeRetryDelayMs({
        attempt,
        retryAfter: response.headers.get("retry-after"),
      }),
      input.signal,
    );
  }
  throw new Error("Cloud runtime connection retry budget was exhausted.");
}

function descriptorNeedsRefresh(
  descriptor: CloudRuntimeConnectionDescriptor | null,
  now: number,
): boolean {
  if (!descriptor?.expiresAt) return true;
  const expiresAt = Date.parse(descriptor.expiresAt);
  return !Number.isFinite(expiresAt) || expiresAt <= now + RUNTIME_TICKET_REFRESH_SKEW_MS;
}

export function createCloudRuntimeCredentialProvider(
  input: CloudRuntimeCredentialProviderInput,
): CloudRuntimeCredentialProvider {
  const requestDescriptor = input.requestDescriptor ?? requestCloudRuntimeConnectionDescriptor;
  const now = input.now ?? Date.now;
  let currentDescriptor = input.initialDescriptor ?? null;
  let refreshPromise: Promise<CloudRuntimeConnectionDescriptor> | null = null;
  let invalidated = false;
  const lifetimeController = new AbortController();
  const raceWithLifetime = <Value>(operation: Promise<Value>) =>
    raceWithAbort(operation, lifetimeController.signal);

  const refresh = (): Promise<CloudRuntimeConnectionDescriptor> => {
    if (refreshPromise) return refreshPromise;

    const nextRefresh = raceWithLifetime(
      requestDescriptor({
        runtimeId: input.runtimeId,
        ...(input.organizationId === undefined ? {} : { organizationId: input.organizationId }),
        signal: lifetimeController.signal,
      }),
    ).then(async (descriptor) => {
      await raceWithLifetime(Promise.resolve(input.validateDescriptor?.(descriptor)));
      await raceWithLifetime(Promise.resolve(input.persistDescriptor?.(descriptor)));
      await raceWithLifetime(Promise.resolve());
      currentDescriptor = descriptor;
      invalidated = false;
      return descriptor;
    });
    refreshPromise = nextRefresh.then(
      (descriptor) => {
        refreshPromise = null;
        return descriptor;
      },
      (error: unknown) => {
        refreshPromise = null;
        throw error;
      },
    );
    return refreshPromise;
  };

  return {
    acquire: (options) => {
      if (lifetimeController.signal.aborted) {
        return raceWithLifetime(Promise.resolve(currentDescriptor!));
      }
      const descriptor =
        options?.forceRefresh || invalidated || descriptorNeedsRefresh(currentDescriptor, now())
          ? refresh()
          : Promise.resolve(currentDescriptor!);
      return raceWithAbort(raceWithLifetime(descriptor), options?.signal);
    },
    invalidate: () => {
      invalidated = true;
    },
    dispose: () => {
      lifetimeController.abort();
    },
  };
}
